import { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Storefront, ArrowLeft } from "@phosphor-icons/react";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerStoreSignup() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", phone: "",
    store_name: "", store_description: "", store_address: "", store_phone: "", store_category: "",
    discount_percentage: "", discount_fixed: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password || !form.phone || !form.store_name || !form.store_address || !form.store_category) {
      toast.error("Please fill all required fields"); return;
    }
    if (form.phone.length !== 10) { toast.error("Phone must be exactly 10 digits"); return; }
    try {
      await axios.post(`${API}/partner-stores/apply-external`, {
        ...form,
        discount_percentage: form.discount_percentage ? parseFloat(form.discount_percentage) : null,
        discount_fixed: form.discount_fixed ? parseFloat(form.discount_fixed) : null
      });
      setSubmitted(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit application"); }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (submitted) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-[#111] border-white/10">
        <CardContent className="p-8 text-center">
          <Storefront className="w-16 h-16 text-bch-gold mx-auto mb-4" weight="duotone" />
          <h2 className="text-white text-xl font-bold mb-2">Application Submitted!</h2>
          <p className="text-white/50 text-sm mb-6">Your partner store application is under review. You'll receive a confirmation email once approved.</p>
          <Link to="/login"><Button className="bg-bch-teal hover:bg-bch-teal/90 text-white">Go to Login</Button></Link>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <SEO title="Partner Store Application - BCH" description="Apply to become a BCH Partner Store" />
      <div className="w-full max-w-lg">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <Card className="bg-[#111] border-white/10">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <Storefront className="w-12 h-12 text-bch-gold mx-auto mb-3" weight="duotone" />
              <h1 className="text-white text-2xl font-bold">Become a Partner Store</h1>
              <p className="text-white/50 text-sm mt-1">Offer exclusive discounts to BCH members</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-white/40 text-[10px] uppercase tracking-widest font-bold">Personal Details</p>
              <Input required placeholder="Full Name *" value={form.full_name} onChange={e => update("full_name", e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-full-name" />
              <Input required type="email" placeholder="Email *" value={form.email} onChange={e => update("email", e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-email" />
              <Input required type="password" placeholder="Password *" value={form.password} onChange={e => update("password", e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-password" />
              <Input required placeholder="Phone (10 digits) *" value={form.phone} onChange={e => update("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-phone" />

              <p className="text-white/40 text-[10px] uppercase tracking-widest font-bold pt-2">Store Details</p>
              <Input required placeholder="Store Name *" value={form.store_name} onChange={e => update("store_name", e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-store-name" />
              <Input required placeholder="Store Category *" value={form.store_category} onChange={e => update("store_category", e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-store-category" />
              <Input required placeholder="Store Address *" value={form.store_address} onChange={e => update("store_address", e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-store-address" />
              <Input placeholder="Store Phone" value={form.store_phone} onChange={e => update("store_phone", e.target.value.replace(/\D/g, "").slice(0, 10))} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-store-phone" />
              <Input placeholder="Description" value={form.store_description} onChange={e => update("store_description", e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />

              <p className="text-white/40 text-[10px] uppercase tracking-widest font-bold pt-2">Discount for BCH Members</p>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" placeholder="Discount %" value={form.discount_percentage} onChange={e => update("discount_percentage", e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-discount-pct" />
                <Input type="number" placeholder="Fixed Discount" value={form.discount_fixed} onChange={e => update("discount_fixed", e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="ps-discount-fixed" />
              </div>

              <Button type="submit" className="w-full bg-bch-gold hover:bg-bch-gold/90 text-black font-bold mt-2" data-testid="ps-submit-btn">Submit Application</Button>
              <p className="text-center text-white/40 text-xs mt-3">Already have an account? <Link to="/login" className="text-bch-teal hover:underline">Login</Link></p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
